import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { FileText } from 'lucide-react';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useTemplateCatalog, useStandardTemplate, useResolvedTemplate,
  useSaveTemplate, useCloneStandard, usePublishTemplate, useRevertTemplate, usePreviewTemplate,
  type CatalogEntry, type HrDocType, type CustomTemplateSummary,
} from '../document-templates/api';
import {
  TemplateStudioHeader,
  TemplateSidebar,
  TemplateScopeBar,
  TemplateVariablesDrawer,
  TemplateEditorWorksurface,
} from '../document-templates/components';

const COMPANY_DEFAULT = '';

/** Finds the custom template row (if any) for a doc type + department. */
function findSummary(entry: CatalogEntry | undefined, department: string): CustomTemplateSummary | undefined {
  return entry?.customTemplates.find((t) => (t.department || '') === department);
}

export function HrDocumentTemplatesPage() {
  const catalog = useTemplateCatalog();
  const [selected, setSelected] = useState<HrDocType | null>(null);
  const [department, setDepartment] = useState<string>(COMPANY_DEFAULT);
  const [bodyHtml, setBodyHtml] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  // Effective selection: the user's choice, or the first doc type once the catalog loads.
  const activeDocType = selected ?? catalog.data?.[0]?.docType ?? null;

  const entry = useMemo(
    () => catalog.data?.find((d) => d.docType === activeDocType),
    [catalog.data, activeDocType],
  );
  const standard = useStandardTemplate(activeDocType);
  const resolved = useResolvedTemplate(activeDocType, department);

  const saveMut = useSaveTemplate();
  const cloneMut = useCloneStandard();
  const publishMut = usePublishTemplate();
  const revertMut = useRevertTemplate();
  const previewMut = usePreviewTemplate();

  const loadedKeyRef = useRef<string>('');

  // Load the effective body into the editor whenever the selection or department changes.
  useEffect(() => {
    if (!activeDocType || !resolved.data) return;
    const key = `${activeDocType}::${department}::${resolved.data.source}::${resolved.data.templateId || 'standard'}`;
    if (loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;
    setBodyHtml(resolved.data.bodyHtml);
    setPreviewHtml(null);
  }, [activeDocType, department, resolved.data]);

  const summary = findSummary(entry, department);
  const hasCustom = !!summary;

  const handlePreview = useCallback(async () => {
    if (!activeDocType) return;
    try {
      const html = await previewMut.mutateAsync({ docType: activeDocType, department, bodyHtml });
      setPreviewHtml(html);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to render document preview.'));
    }
  }, [activeDocType, department, bodyHtml, previewMut]);

  // Auto-load preview when template or department changes
  useEffect(() => {
    if (activeDocType && bodyHtml && !previewHtml && !previewMut.isPending) {
      handlePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocType, department]);

  async function handleSave() {
    if (!activeDocType) return;
    try {
      const origin = summary?.origin === 'standard_clone' ? 'standard_clone' : 'custom';
      const res = await saveMut.mutateAsync({
        docType: activeDocType,
        department,
        name: entry?.label || activeDocType,
        bodyHtml,
        origin,
      });
      toast.success(res.sanitized ? 'Saved as draft. Unsafe markup removed for security.' : 'Saved as draft.');
      loadedKeyRef.current = '';
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save template draft.'));
    }
  }

  async function handlePublish() {
    if (!activeDocType) return;
    try {
      // Save current edits first, then publish, so the live copy matches the editor.
      await saveMut.mutateAsync({
        docType: activeDocType,
        department,
        name: entry?.label || activeDocType,
        bodyHtml,
      });
      await publishMut.mutateAsync({ docType: activeDocType, department });
      toast.success('Template published live — it is now used for HR document generation.');
      loadedKeyRef.current = '';
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to publish template.'));
    }
  }

  async function handleClone() {
    if (!activeDocType) return;
    try {
      const row = await cloneMut.mutateAsync({ docType: activeDocType, department });
      setBodyHtml(row.bodyHtml);
      toast.success('Cloned the ZYNX standard into an editable draft.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to clone standard template.'));
    }
  }

  async function handleRevert() {
    if (!activeDocType) return;
    setConfirmReset(false);
    try {
      await revertMut.mutateAsync({ docType: activeDocType, department });
      toast.success('Reverted to the ZYNX standard.');
      loadedKeyRef.current = '';
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to revert template.'));
    }
  }

  function loadStandardIntoEditor() {
    if (!standard.data) return;
    setBodyHtml(standard.data.bodyHtml);
    toast.success('Loaded ZYNX standard into the editor. Save draft to keep your changes.');
  }

  function handleInsertToken(token: string) {
    const snippet = `{{${token}}}`;
    setBodyHtml((prev) => prev + snippet);
  }

  // Global Ctrl+S / Cmd+S keyboard shortcut to save draft
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (activeDocType && !saveMut.isPending) {
          handleSave();
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeDocType, bodyHtml, department, saveMut.isPending]);

  return (
    <div className="space-y-6 max-w-[1580px] mx-auto pb-16">
      {/* 1. Hero / Header Banner */}
      <TemplateStudioHeader
        catalog={catalog.data}
        isLoading={catalog.isLoading}
        activeDepartment={department}
      />

      {/* 2. Main Studio Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 items-start">
        {/* Left Column: Document Type Navigation Sidebar */}
        <div className="lg:sticky lg:top-6">
          <TemplateSidebar
            catalog={catalog.data}
            isLoading={catalog.isLoading}
            isError={catalog.isError}
            selected={activeDocType}
            onSelect={(docType) => setSelected(docType)}
            department={department}
          />
        </div>

        {/* Right Column: Active Document Studio Area */}
        {!activeDocType ? (
          <Card className="shadow-sm border-outline">
            <CardBody className="flex flex-col items-center justify-center py-20 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/40 mb-3" />
              <h3 className="text-base font-semibold text-on-surface">No Document Selected</h3>
              <p className="text-sm text-muted-foreground max-w-sm mt-1">
                Select an HR document type from the catalog on the left to view, edit, or customize its template.
              </p>
            </CardBody>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Scope & Actions Toolbar */}
            <TemplateScopeBar
              activeDocLabel={entry?.label || activeDocType}
              department={department}
              onDepartmentChange={setDepartment}
              summary={summary}
              hasCustom={hasCustom}
              canLoadStandard={!!standard.data}
              onLoadStandard={loadStandardIntoEditor}
              onClone={handleClone}
              onSave={handleSave}
              onPublish={handlePublish}
              onResetConfirm={() => setConfirmReset(true)}
              isSaving={saveMut.isPending}
              isPublishing={publishMut.isPending}
              isCloning={cloneMut.isPending}
              isReverting={revertMut.isPending}
            />

            {/* Dynamic Variable Tokens Palette */}
            <TemplateVariablesDrawer
              variables={entry?.variables}
              onInsertToken={handleInsertToken}
            />

            {/* Multi-mode Editor Worksurface & Live Preview */}
            {resolved.isLoading ? (
              <Card className="shadow-sm border-outline">
                <CardBody className="p-8 space-y-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-48" />
                    <Skeleton className="h-10 w-24" />
                  </div>
                  <Skeleton className="h-96 w-full rounded-xl" />
                </CardBody>
              </Card>
            ) : (
              <TemplateEditorWorksurface
                bodyHtml={bodyHtml}
                onChangeHtml={setBodyHtml}
                previewHtml={previewHtml}
                onRefreshPreview={handlePreview}
                isRenderingPreview={previewMut.isPending}
              />
            )}
          </div>
        )}
      </div>

      {/* Reset Confirmation Dialog */}
      <ConfirmDialog
        open={confirmReset}
        title="Reset to ZYNX Standard?"
        message="This will delete the custom template for this scope. Documents will revert to using the ZYNX default standard template. This action cannot be undone."
        confirmLabel="Reset to Standard"
        danger
        loading={revertMut.isPending}
        onConfirm={handleRevert}
        onCancel={() => setConfirmReset(false)}
      />
    </div>
  );
}
